import React from 'react';
import Cookies from 'js-cookie';

import ExtraQuestionsWidget from './ExtraQuestionsWidget';
import BubbleMessage from './v3/BubbleMessage';
import BubbleMessageStack from './v3/BubbleMessageStack';

/** EditForm component. Receives props:

`current_questions` - object of the current questions available for the form. Keyed with question ID,
translated to the set locale. Example:
```
{
   178: {
     "answered": true,
     "area_id": 8,
     "active": true,
     "text": "Olen",
     "order": 0,
     "params": "boy,girl,unknown,muu",
     "type": "SO",
     "exclude_in_short_format": false
   }
 }
 ```

 `question_areas` - list of the questions areas. Example:
 ```
 {
     "id": 8,
     "category": "Perustietoa",
     "mandatory": true
 }
 ```
 */
export default class extends React.Component {
  static defaultProps = {
    id: null,
    name: "", description: "", greeting_text: "", target_audience_display: "",
    question_areas: [], current_questions: {}, questionnaire_types: []
  };

  constructor(props) {
    super(props)
    console.log("EditForm received", props)
    const areasUsed = Object.values(props.current_questions).filter((q) => q.active === true).reduce((acc, q) => acc.add(q.area_id), new Set())
    console.log("Areas used in edit form:", areasUsed)
    this.state = {
      selectedAreas: Array.from(areasUsed),
      message: null,
    }
    this.formRef = React.createRef()
    this.toggleSelect = this.toggleSelect.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  componentWillUnmount() {
    clearTimeout(this.messageTimeout)
    clearTimeout(this.messageFadeTimeout)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.question_areas !== this.props.question_areas) {
      this.setState({
        selectedAreas: this.props.question_areas.map((area) => area.id)
      })
    }
  }

  toggleSelect(qaId) {
    let newSelected = this.state.selectedAreas.slice()
    if (this.state.selectedAreas.includes(qaId)) {
      newSelected = newSelected.filter((id) => id !== qaId)
    } else {
      newSelected.push(qaId)
    }
    this.setState({ selectedAreas: newSelected })
  }

  handleSubmit(e) {
    e.preventDefault();
    const formData = new FormData(this.formRef.current);
    const extraTexts = formData.getAll('extra_question_texts[]');
    const extraTypes = formData.getAll('extra_question_types[]');
    const extraIds = formData.getAll('extra_question_ids[]');
    const extraActives = formData.getAll('extra_question_actives[]');
    const newQuestions = extraTexts
      .map((text, i) => ({ text, type: extraTypes[i], order: i, id: extraIds[i] || null, active: extraActives[i] !== 'false' }))
      .filter((q) => q.active)
      .map(({ active, ...question }) => question);
    const data = {
      audience: formData.get('audience'),
      name: formData.get('name'),
      description: formData.get('description'),
      greeting_text: formData.get('greeting_text'),
      question_areas: this.state.selectedAreas,
      extra_questions: newQuestions,
    };

    console.log("sending to the backend", data)

    const csrftoken = Cookies.get('csrftoken');
    fetch(
      `/api/v1/forms/${this.props.id}`,
      {
        method: 'PATCH',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
          'X-CSRFToken': csrftoken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      .then((response) => response.json().then((obj) => ({ ok: response.ok, obj })))
      .then(({ ok, obj }) => {
        if (!ok || obj.error) {
          alert(I18N._("Failed to save questionnaire") + ": " + (obj.error || JSON.stringify(obj)));
        } else {
          clearTimeout(this.messageTimeout)
          clearTimeout(this.messageFadeTimeout)
          this.setState({ message: { text: I18N._("Questionnaire saved successfully"), type: "success", visible: true } });
          this.messageTimeout = setTimeout(() => {
            // fade the bubble out first, then unmount it once the transition has finished
            this.setState((prevState) => ({
              message: prevState.message && { ...prevState.message, visible: false }
            }));
            this.messageFadeTimeout = setTimeout(() => this.setState({ message: null }), 300);
          }, 4000);
        }
      })
      .catch((err) => {
        console.error(err);
        alert(I18N._("Error requesting data from backend") + ": " + err);
      });
  }

  render() {
    const totalQuestions = this.props.question_areas
      .filter((qa) => this.state.selectedAreas.includes(qa.id))
      .reduce((col, area) => area.questions.length + col, 0);

    const validAreaIds = new Set(this.props.question_areas.map((area) => area.id))
    console.log("Valid area ids to use in the edit form", validAreaIds)
    // some forms might have old questions part from older templates, show those under extras
    const extraQuestions = Object.values(this.props.current_questions)
      .filter((q) => q.area_id === null || !validAreaIds.has(q.area_id))

    return (
      <React.Fragment>
        <BubbleMessageStack>
          {this.state.message &&
            <BubbleMessage text={this.state.message.text} type={this.state.message.type} visible={this.state.message.visible} />}
        </BubbleMessageStack>
        <form className="form-horizontal" ref={this.formRef} onSubmit={this.handleSubmit}>

          <div className="form-group even vertical-padding target-audience">
            <div className="col-sm-4 col-xs-12">
              <label className="control-label">
                {I18N._("Questionnaire audience")}
              </label>
              <p className="help-block">
                {I18N._("The target audience for this questionnaire. The same questionnaire can and should be used year after year so that changes in results can easily be seen.")}
              </p>
            </div>
            <div className="col-sm-8 col-xs-12">
              <b>{this.props.target_audience_display}</b>
            </div>
          </div>

          <div className="form-group even vertical-padding question-areas-container">
            <div className="col-sm-4 col-xs-12">
              <label className="control-label">
                {I18N._("Included areas")}
              </label>
              <p className="help-block">
                <span dangerouslySetInnerHTML={{ __html: I18N._("There are <span>%(questions_in_total)s</span> questions in total", { params: { questions_in_total: totalQuestions } }) }}>
                </span>.
                <br />
                <br />
                {I18N._("Including all question areas is recommended")}
              </p>
            </div>
            <div className="col-sm-8 col-xs-12 question-areas">
              <QuestionsWithAreasList
                current_questions={this.props.current_questions}
                question_areas={this.props.question_areas}
                selectedAreas={this.state.selectedAreas}
                onToggleSelect={this.toggleSelect} />
            </div>
          </div>

          <div className="form-group odd vertical-padding">
            <div className="col-sm-4 col-xs-12">
              <label className="control-label">
                {I18N._("Extra questions")}
              </label>
              <p className="help-block">
                {I18N._("Optional extra questions will only show up in this questionnaire")}
              </p>
            </div>
            <div className="col-sm-8 col-xs-12">
              <ExtraQuestionsWidget questions={extraQuestions} />
            </div>
          </div>

          <div className="form-group even vertical-padding">
            <div className="col-sm-4 col-xs-12">
              <label htmlFor="name" className="control-label">
                {I18N._("Questionnaire name")}
              </label>
            </div>
            <div className="col-sm-8 col-xs-12">
              <input type="text" id="name" name="name" className="form-control"
                defaultValue={this.props.name}
                placeholder={I18N._("Optional name for the questionnaire")} />
            </div>
          </div>

          <div className="form-group odd vertical-padding">
            <div className="col-sm-4 col-xs-12">
              <label htmlFor="description" className="control-label">
                {I18N._("Questionnaire description")}
              </label>
              <p className="help-block">
                {I18N._("An optional description for internal use.")}
              </p>
            </div>
            <div className="col-sm-8 col-xs-12">
              <textarea id="description" name="description" className="form-control"
                defaultValue={this.props.description}></textarea>
            </div>
          </div>

          <div className="form-group even vertical-padding">
            <div className="col-sm-4 col-xs-12">
              <label htmlFor="greeting_text" className="control-label">
                {I18N._("Questionnaire greeting")}
              </label>
              <p className="help-block">
                {I18N._("An optional greeting text that will be shown on the top of the form.")}
              </p>
            </div>
            <div className="col-sm-8 col-xs-12">
              <textarea id="greeting_text" name="greeting_text" className="form-control"
                defaultValue={this.props.greeting_text}></textarea>
            </div>
          </div>

          <div className="row text-center">
            <div className="button-group">
              <input type="submit" className="btn btn-success" value={I18N._("Save questionnaire")} />
            </div>
          </div>
        </form>
      </React.Fragment>
    )
  }
}

class QuestionsWithAreasList extends React.Component {
  static defaultProps = { current_questions: {}, question_areas: [], selectedAreas: [], onToggleSelect: () => { } };

  render() {
    const validAreaIds = new Set(this.props.question_areas.map((area) => area.id))

    const grouped = Object.values(this.props.current_questions).reduce((acc, q) => {
      if (q.area_id !== null && validAreaIds.has(q.area_id)) {
        acc[q.area_id] = acc[q.area_id] || {};
        acc[q.area_id][q.id] = q;
      }
      return acc;
    }, {});

    // question_areas carries the full set of questions available in each area, including
    // ones not yet part of this form's current_questions - merge those in too so they can
    // be picked from the area listing.
    this.props.question_areas.forEach((area) => {
      const areaQuestions = grouped[area.id] = grouped[area.id] || {};
      (area.questions || []).forEach((q) => {
        areaQuestions[q.id] = areaQuestions[q.id] || q;
      });
    });

    return (
      <div className="questions-list">
        {
          Object.keys(grouped).map((areaId) =>
            <FormQuestionArea
              key={areaId}
              areaId={parseInt(areaId, 10)}
              questions={Object.values(grouped[areaId])}
              question_areas={this.props.question_areas}
              selected={this.props.selectedAreas.includes(parseInt(areaId, 10))}
              onToggleSelect={() => this.props.onToggleSelect(parseInt(areaId, 10))} />
          )
        }
      </div>
    )
  }
}

class FormQuestionArea extends React.Component {
  state = { expanded: false };

  toggleExpanded = (e) => {
    e.preventDefault();
    this.setState({ expanded: !this.state.expanded });
    return false;
  };

  render() {
    const area = this.props.question_areas.find((qa) => qa.id === this.props.areaId);
    const areaName = area ? area.category : I18N._("Unknown area");
    const mandatory = area && area.mandatory;
    return (
      <label className={"col-xs-12 form-group " + (mandatory ? 'mandatory' : '')}>
        <input
          type="checkbox"
          id={"question-area-" + this.props.areaId}
          name={"question_areas#" + this.props.areaId}
          checked={mandatory ? true : this.props.selected}
          onChange={this.props.onToggleSelect}
          disabled={mandatory}
        />
        {areaName}
        <a href="#" onClick={this.toggleExpanded}>
          {this.props.questions.length} {I18N._("questions")}
          <span
            className={"glyphicon glyphicon-chevron-" + (this.state.expanded ? "up" : "down")}
            aria-hidden="true">

          </span>
        </a>
        {
          this.state.expanded ?
            <ul className="questions-list">
              {
                this.props.questions.map((q, i) =>
                  <li key={i}>
                    <span
                      className={"glyphicon " + (q.answered ? "glyphicon-ok" : "glyphicon-remove")}
                      aria-hidden="true">
                    </span>
                    {q.text}
                  </li>
                )
              }
            </ul>
            : ''
        }
      </label>
    )
  }
}
